import React, { useCallback, useEffect, useRef, useState } from 'react';
import './VideoPlayer.css';

export interface VideoPlayerProps {
  src: string;
  poster?: string;
  /** Accessible name for the player region. */
  title?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  /** Live stream: hides the scrubber, speed and section-loop controls. */
  live?: boolean;
  /** Force HLS handling. Auto-detected for `.m3u8` sources. */
  hls?: boolean;
  /** How the picture fills the player box. */
  fit?: 'contain' | 'cover';
  /**
   * Where keyboard shortcuts listen. 'focus' (default) only while the player has
   * focus; 'global' anywhere on the page (use inside a modal that owns the screen).
   */
  keyboard?: 'focus' | 'global';
  /** Minimal control bar for small embeds (play, scrubber, mute, fullscreen). */
  compact?: boolean;
  /**
   * Size the player to the video's own aspect ratio (portrait or landscape),
   * capped at `maxHeight`, instead of filling a box sized by the parent.
   */
  intrinsic?: boolean;
  /** Height cap for intrinsic sizing (any CSS length). Default 75vh. */
  maxHeight?: string;
  className?: string;
  /** Fired on unrecoverable playback errors (e.g. expired signed URL). */
  onFatalError?: () => void;
}

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5];
const SEEK_STEP = 5;
const FRAME_STEP = 1 / 30;
const HIDE_DELAY = 2500;

function formatTime(t: number): string {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

const Icon: React.FC<{ d: string; size?: number }> = ({ d, size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d={d} />
  </svg>
);

const ICONS = {
  play: 'M8 5.14v13.72a1 1 0 0 0 1.52.85l10.76-6.86a1 1 0 0 0 0-1.7L9.52 4.29A1 1 0 0 0 8 5.14z',
  pause: 'M7 5h3.5v14H7zM13.5 5H17v14h-3.5z',
  back: 'M11.5 12 20 18V6l-8.5 6zM4 6v12h2V6H4z',
  volume: 'M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06A9 9 0 0 0 14 3.23z',
  muted: 'M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0 0 21 12a9 9 0 0 0-7-8.77v2.06A7 7 0 0 1 19 12zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z',
  fullscreen: 'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z',
  exitFullscreen: 'M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z',
  mirror: 'M15 21h2v-2h-2v2zm4-12h2V7h-2v2zM3 5v14c0 1.1.9 2 2 2h4v-2H5V5h4V3H5c-1.1 0-2 .9-2 2zm16-2v2h2c0-1.1-.9-2-2-2zm-8 20h2V1h-2v22zm8-6h2v-2h-2v2zM15 5h2V3h-2v2zm4 8h2v-2h-2v2zm0 8c1.1 0 2-.9 2-2h-2v2z',
  loop: 'M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z',
  frameBack: 'M6 6h2v12H6zm3.5 6 8.5 6V6z',
  frameFwd: 'M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z',
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  poster,
  title,
  autoPlay = false,
  loop = false,
  muted: initialMuted = false,
  live = false,
  hls: forceHls,
  fit = 'contain',
  keyboard = 'focus',
  compact = false,
  intrinsic = false,
  maxHeight = '75vh',
  className = '',
  onFatalError,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | undefined>(undefined);
  const onFatalErrorRef = useRef(onFatalError);
  onFatalErrorRef.current = onFatalError;

  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [muted, setMuted] = useState(initialMuted);
  const [autoMuted, setAutoMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [mirrored, setMirrored] = useState(false);
  const [looping, setLooping] = useState(loop);
  const [loopA, setLoopA] = useState<number | null>(null);
  const [loopB, setLoopB] = useState<number | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [hover, setHover] = useState<{ x: number; t: number } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [aspect, setAspect] = useState(16 / 9);
  const flashTimer = useRef<number | undefined>(undefined);

  const isHls = forceHls ?? /\.m3u8(\?|$)/i.test(src);
  const sectionActive = loopA !== null && loopB !== null && loopB > loopA;

  // ---- Source / HLS wiring -------------------------------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    setFailed(false);
    setLoopA(null);
    setLoopB(null);
    // React doesn't reliably reflect the `muted` attribute — set the property.
    video.muted = initialMuted;
    setMuted(initialMuted);

    const tryPlay = () => {
      if (!autoPlay) return;
      video.play().catch(() => {
        // Autoplay with sound blocked — retry muted; the user can unmute.
        video.muted = true;
        setMuted(true);
        setAutoMuted(true);
        video.play().catch(() => {});
      });
    };

    if (!isHls || video.canPlayType('application/vnd.apple.mpegurl')) {
      // Plain file, or native HLS (Safari/iOS handles LL-HLS well natively).
      video.src = src;
      tryPlay();
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    let destroyed = false;
    let instance: { destroy: () => void } | null = null;
    import('hls.js').then(({ default: Hls }) => {
      if (destroyed) return;
      if (!Hls.isSupported()) {
        setFailed(true);
        onFatalErrorRef.current?.();
        return;
      }
      const hls = new Hls({ lowLatencyMode: live, liveSyncDurationCount: 3, enableWorker: true });
      instance = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }
        setFailed(true);
        onFatalErrorRef.current?.();
      });
    });
    return () => {
      destroyed = true;
      instance?.destroy();
    };
  }, [src, isHls, live, autoPlay, initialMuted]);

  // ---- Media element events ------------------------------------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onMeta = () => {
      setDuration(video.duration);
      if (video.videoWidth && video.videoHeight) setAspect(video.videoWidth / video.videoHeight);
    };
    const onWaiting = () => setWaiting(true);
    const onReady = () => setWaiting(false);
    const onProgress = () => {
      const b = video.buffered;
      if (b.length) setBuffered(b.end(b.length - 1));
    };
    const onVolume = () => setMuted(video.muted);
    const onError = () => {
      // hls.js reports its own fatal errors; only native playback lands here.
      if (!video.error) return;
      setFailed(true);
      onFatalErrorRef.current?.();
    };
    const onTime = () => setTime(video.currentTime);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('durationchange', onMeta);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onReady);
    video.addEventListener('canplay', onReady);
    video.addEventListener('progress', onProgress);
    video.addEventListener('volumechange', onVolume);
    video.addEventListener('error', onError);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('seeked', onTime);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('durationchange', onMeta);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onReady);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('volumechange', onVolume);
      video.removeEventListener('error', onError);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', onTime);
    };
  }, []);

  // Smooth progress + frame-accurate A–B looping while playing. `timeupdate`
  // only fires ~4×/s, which would overshoot the B point by up to 250ms.
  useEffect(() => {
    if (!playing) return;
    const video = videoRef.current;
    if (!video) return;
    let raf = 0;
    const tick = () => {
      if (sectionActive && video.currentTime >= (loopB as number)) {
        video.currentTime = loopA as number;
      }
      setTime(video.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, sectionActive, loopA, loopB]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = rate;
  }, [rate]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.loop = looping;
  }, [looping]);

  // ---- Fullscreen tracking -------------------------------------------------
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ---- Auto-hiding controls ------------------------------------------------
  const pokeControls = useCallback(() => {
    setControlsVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), HIDE_DELAY);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(hideTimer.current);
    window.clearTimeout(flashTimer.current);
  }, []);

  const showControls = controlsVisible || !playing || speedMenuOpen || failed;

  const showFlash = useCallback((msg: string) => {
    setFlash(msg);
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 900);
  }, []);

  // ---- Actions -------------------------------------------------------------
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused || video.ended) {
      if (autoMuted) {
        // A deliberate tap is a user gesture — bring the sound back.
        video.muted = false;
        setAutoMuted(false);
      }
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [autoMuted]);

  const seekTo = useCallback((t: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    video.currentTime = Math.min(Math.max(t, 0), video.duration);
    setTime(video.currentTime);
  }, []);

  const seekBy = useCallback((delta: number) => {
    const video = videoRef.current;
    if (video) seekTo(video.currentTime + delta);
  }, [seekTo]);

  const stepFrame = useCallback((dir: 1 | -1) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    seekTo(video.currentTime + dir * FRAME_STEP);
  }, [seekTo]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setAutoMuted(false);
  }, []);

  const changeRate = useCallback((next: number) => {
    setRate(next);
    showFlash(`${next}×`);
  }, [showFlash]);

  const nudgeRate = useCallback((dir: 1 | -1) => {
    const i = SPEEDS.indexOf(rate);
    const next = SPEEDS[Math.min(Math.max((i === -1 ? 3 : i) + dir, 0), SPEEDS.length - 1)];
    changeRate(next);
  }, [rate, changeRate]);

  const toggleMirror = useCallback(() => {
    setMirrored((m) => {
      showFlash(m ? 'Mirror off' : 'Mirrored');
      return !m;
    });
  }, [showFlash]);

  // Section loop cycles: set A → set B → clear.
  const cycleSection = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const now = video.currentTime;
    if (loopA === null) {
      setLoopA(now);
      setLoopB(null);
      showFlash('Loop start set');
    } else if (loopB === null) {
      if (now <= loopA + 0.1) {
        // B must come after A — treat as a fresh A.
        setLoopA(now);
        showFlash('Loop start set');
        return;
      }
      setLoopB(now);
      video.currentTime = loopA;
      showFlash('Looping section');
    } else {
      setLoopA(null);
      setLoopB(null);
      showFlash('Section cleared');
    }
  }, [loopA, loopB, showFlash]);

  const toggleFullscreen = useCallback(() => {
    const root = rootRef.current;
    const video = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!root || !video) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (root.requestFullscreen) {
      root.requestFullscreen().catch(() => {});
    } else if (video.webkitEnterFullscreen) {
      // iOS Safari only allows fullscreen on the <video> itself.
      video.webkitEnterFullscreen();
    }
  }, []);

  // ---- Keyboard shortcuts --------------------------------------------------
  const handleKey = useCallback((e: KeyboardEvent | React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
    // Let buttons keep Space/Enter activation when they have focus.
    const onButton = e.target instanceof HTMLElement && e.target.closest('button, [role="slider"]');
    const key = e.key;
    let handled = true;
    switch (key) {
      case ' ':
      case 'k':
      case 'K':
        if (onButton && key === ' ') { handled = false; break; }
        togglePlay();
        break;
      case 'ArrowLeft':
      case 'j':
      case 'J':
        if (live) { handled = false; break; }
        seekBy(-SEEK_STEP);
        break;
      case 'ArrowRight':
      case 'l':
      case 'L':
        if (live) { handled = false; break; }
        seekBy(SEEK_STEP);
        break;
      case ',':
        if (live) { handled = false; break; }
        stepFrame(-1);
        break;
      case '.':
        if (live) { handled = false; break; }
        stepFrame(1);
        break;
      case '[':
        if (live) { handled = false; break; }
        nudgeRate(-1);
        break;
      case ']':
        if (live) { handled = false; break; }
        nudgeRate(1);
        break;
      case 'm':
      case 'M':
        toggleMute();
        break;
      case 'f':
      case 'F':
        toggleFullscreen();
        break;
      case 'r':
      case 'R':
        toggleMirror();
        break;
      case 'a':
      case 'A':
        if (live) { handled = false; break; }
        cycleSection();
        break;
      default:
        if (!live && /^[0-9]$/.test(key)) {
          const video = videoRef.current;
          if (video && Number.isFinite(video.duration)) seekTo((Number(key) / 10) * video.duration);
        } else {
          handled = false;
        }
    }
    if (handled) {
      e.preventDefault();
      pokeControls();
    }
  }, [live, togglePlay, seekBy, stepFrame, nudgeRate, toggleMute, toggleFullscreen, toggleMirror, cycleSection, seekTo, pokeControls]);

  useEffect(() => {
    if (keyboard !== 'global') return;
    const onKey = (e: KeyboardEvent) => {
      // Don't steal keys from other widgets inside the player — the root's own
      // handler covers those via bubbling.
      if (rootRef.current?.contains(e.target as Node)) return;
      handleKey(e);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [keyboard, handleKey]);

  // ---- Scrubber ------------------------------------------------------------
  const fractionAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
  };

  const onTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!duration) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekTo(fractionAt(e.clientX) * duration);
  };

  const onTrackPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!duration) return;
    const f = fractionAt(e.clientX);
    const rect = trackRef.current!.getBoundingClientRect();
    setHover({ x: f * rect.width, t: f * duration });
    if (e.currentTarget.hasPointerCapture(e.pointerId)) seekTo(f * duration);
  };

  const onTrackKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { seekBy(-SEEK_STEP); e.preventDefault(); e.stopPropagation(); }
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { seekBy(SEEK_STEP); e.preventDefault(); e.stopPropagation(); }
    if (e.key === 'Home') { seekTo(0); e.preventDefault(); e.stopPropagation(); }
    if (e.key === 'End') { seekTo(duration); e.preventDefault(); e.stopPropagation(); }
  };

  // ---- Surface interaction -------------------------------------------------
  const lastPointer = useRef<string>('mouse');
  const onSurfaceClick = () => {
    // On touch, the first tap only reveals the controls.
    if (lastPointer.current === 'touch' && !showControls) {
      pokeControls();
      return;
    }
    togglePlay();
    pokeControls();
  };

  const sizeStyle: React.CSSProperties | undefined = intrinsic
    ? { aspectRatio: String(aspect), width: `min(100%, calc(${maxHeight} * ${aspect}))`, height: 'auto', marginInline: 'auto' }
    : undefined;

  const pct = (t: number) => (duration ? `${(t / duration) * 100}%` : '0%');

  return (
    <div
      ref={rootRef}
      className={[
        'vp',
        `vp--${fit}`,
        compact ? 'vp--compact' : '',
        showControls ? 'vp--controls' : '',
        fullscreen ? 'vp--fullscreen' : '',
        intrinsic ? 'vp--intrinsic' : '',
        className,
      ].join(' ')}
      style={sizeStyle}
      role="region"
      aria-label={title ? `Video player: ${title}` : 'Video player'}
      tabIndex={0}
      onKeyDown={handleKey}
      onPointerMove={pokeControls}
      onPointerDown={(e) => { lastPointer.current = e.pointerType; }}
      onMouseLeave={() => playing && setControlsVisible(false)}
    >
      <video
        ref={videoRef}
        className={`vp-video ${mirrored ? 'vp-video--mirrored' : ''}`}
        poster={poster}
        muted={initialMuted}
        playsInline
        preload="metadata"
        onClick={onSurfaceClick}
        onDoubleClick={compact ? undefined : toggleFullscreen}
      />

      {waiting && !failed && <div className="vp-spinner" aria-hidden="true" />}

      {failed && (
        <div className="vp-error" role="alert">
          <strong>Video unavailable</strong>
          <span>It may still be processing, or the link has expired.</span>
        </div>
      )}

      {!playing && !failed && !waiting && (
        <button type="button" className="vp-bigplay" onClick={togglePlay} aria-label="Play">
          <Icon d={ICONS.play} size={36} />
        </button>
      )}

      {autoMuted && playing && (
        <button type="button" className="vp-unmute" onClick={toggleMute}>
          <Icon d={ICONS.muted} size={18} />
          Tap to unmute
        </button>
      )}

      {flash && <div className="vp-flash" aria-live="polite">{flash}</div>}

      {live && <span className="vp-live">Live</span>}

      <div className="vp-controls" onClick={(e) => e.stopPropagation()}>
        {!live && (
          <div
            ref={trackRef}
            className="vp-track"
            role="slider"
            tabIndex={0}
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration) || 0}
            aria-valuenow={Math.round(time)}
            aria-valuetext={`${formatTime(time)} of ${formatTime(duration)}`}
            onPointerDown={onTrackPointerDown}
            onPointerMove={onTrackPointerMove}
            onPointerLeave={() => setHover(null)}
            onKeyDown={onTrackKeyDown}
          >
            <div className="vp-track-rail">
              <div className="vp-track-buffered" style={{ width: pct(buffered) }} />
              {loopA !== null && (
                <div
                  className={`vp-track-section ${sectionActive ? 'is-active' : ''}`}
                  style={{ left: pct(loopA), width: sectionActive ? `calc(${pct(loopB as number)} - ${pct(loopA)})` : '2px' }}
                />
              )}
              <div className="vp-track-played" style={{ width: pct(time) }} />
            </div>
            <div className="vp-track-thumb" style={{ left: pct(time) }} />
            {hover && (
              <div className="vp-track-tooltip" style={{ left: hover.x }}>{formatTime(hover.t)}</div>
            )}
          </div>
        )}

        <div className="vp-bar">
          <button type="button" className="vp-btn" onClick={togglePlay} aria-label={playing ? 'Pause (K)' : 'Play (K)'} title={playing ? 'Pause (K)' : 'Play (K)'}>
            <Icon d={playing ? ICONS.pause : ICONS.play} />
          </button>

          {!live && !compact && (
            <>
              <button type="button" className="vp-btn vp-btn--hide-sm" onClick={() => stepFrame(-1)} aria-label="Previous frame (,)" title="Previous frame (,)">
                <Icon d={ICONS.frameBack} size={18} />
              </button>
              <button type="button" className="vp-btn vp-btn--hide-sm" onClick={() => stepFrame(1)} aria-label="Next frame (.)" title="Next frame (.)">
                <Icon d={ICONS.frameFwd} size={18} />
              </button>
            </>
          )}

          <button type="button" className="vp-btn" onClick={toggleMute} aria-label={muted ? 'Unmute (M)' : 'Mute (M)'} title={muted ? 'Unmute (M)' : 'Mute (M)'}>
            <Icon d={muted ? ICONS.muted : ICONS.volume} size={20} />
          </button>

          {!live && (
            <span className="vp-time">
              {formatTime(time)} <span className="vp-time-sep">/</span> {formatTime(duration)}
            </span>
          )}

          <span className="vp-spacer" />

          {!live && !compact && (
            <>
              <button
                type="button"
                className={`vp-btn vp-chip ${loopA !== null ? 'is-on' : ''}`}
                onClick={cycleSection}
                aria-label={loopA === null ? 'Set loop start (A)' : loopB === null ? 'Set loop end (A)' : 'Clear section loop (A)'}
                title={loopA === null ? 'Set loop start (A)' : loopB === null ? 'Set loop end (A)' : 'Clear section loop (A)'}
              >
                {loopA === null ? 'A–B' : loopB === null ? 'Set B' : 'A–B ✕'}
              </button>
              <button
                type="button"
                className={`vp-btn ${looping ? 'is-on' : ''}`}
                onClick={() => setLooping((l) => !l)}
                aria-pressed={looping}
                aria-label="Loop video"
                title="Loop video"
              >
                <Icon d={ICONS.loop} size={20} />
              </button>
            </>
          )}

          {!live && (
            <div className="vp-speed">
              <button
                type="button"
                className={`vp-btn vp-chip ${rate !== 1 ? 'is-on' : ''}`}
                onClick={() => setSpeedMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={speedMenuOpen}
                aria-label={`Playback speed ${rate}× ([ and ])`}
                title="Playback speed ([ / ])"
              >
                {rate}×
              </button>
              {speedMenuOpen && (
                <div className="vp-menu" role="menu">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="menuitemradio"
                      aria-checked={s === rate}
                      className={`vp-menu-item ${s === rate ? 'is-on' : ''}`}
                      onClick={() => { changeRate(s); setSpeedMenuOpen(false); }}
                    >
                      {s === 1 ? 'Normal' : `${s}×`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!compact && (
            <button
              type="button"
              className={`vp-btn ${mirrored ? 'is-on' : ''}`}
              onClick={toggleMirror}
              aria-pressed={mirrored}
              aria-label="Mirror video (R)"
              title="Mirror video (R)"
            >
              <Icon d={ICONS.mirror} size={20} />
            </button>
          )}

          <button type="button" className="vp-btn" onClick={toggleFullscreen} aria-label={fullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'} title={fullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}>
            <Icon d={fullscreen ? ICONS.exitFullscreen : ICONS.fullscreen} size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
